import { FC } from "react";
import { Alert, Box, Typography } from "@mui/material";

import {
  ClaimLinkPreview,
  ClaimStatus,
  FeeChange,
} from "../../../../../types/claims";
import { formatDisplayDate } from "../../../../helpers/dataHelper";
import StatusChip from "../../table-components/StatusChip";
import {
  BASELINE_FALLBACK_MESSAGES,
  formatFeeAmount,
  formatFeeMonth,
} from "../claimAppeals";
import {
  BeforeAfter,
  EffectPanel,
  SummaryRow,
} from "../record-payment/ClaimEditDialogParts";
import { claimTag, describeEarlyRemits, describeLink } from "./claimLinks";

const Status: FC<{ status: string | null }> = ({ status }) =>
  status ? <StatusChip status={status as ClaimStatus} /> : <>—</>;

/** The original's paid amount, status and success fee, before and after. */
const LinkFeeEffect: FC<{ fee: FeeChange; parent: string; child: string }> = ({
  fee,
  parent,
  child,
}) => (
  <Box>
    <SummaryRow label="Paid amount">
      <BeforeAfter
        before={formatFeeAmount(fee.paidAmount.before)}
        after={formatFeeAmount(fee.paidAmount.after)}
      />
    </SummaryRow>
    {fee.claimStatus.before !== fee.claimStatus.after && (
      <SummaryRow label="Status">
        <BeforeAfter
          same={false}
          before={<Status status={fee.claimStatus.before} />}
          after={<Status status={fee.claimStatus.after} />}
        />
      </SummaryRow>
    )}
    <SummaryRow label="Success fee">
      <BeforeAfter
        before={formatFeeAmount(fee.successFee.before)}
        after={formatFeeAmount(fee.successFee.after)}
      />
    </SummaryRow>
    <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.5 }}>
      {fee.changed
        ? `Counts ${child}'s payments after the action.`
        : `This correct and resubmit doesn't carry ${parent}'s success fee, so linking doesn't change it.`}
      {fee.restatedMonths.length > 0 &&
        ` Restates the success fee already booked for ${fee.restatedMonths.map(formatFeeMonth).join(", ")}.`}
    </Typography>
    {fee.baselineFallback && (
      <Alert severity="warning" sx={{ maxWidth: "100%", mt: 1 }}>
        {BASELINE_FALLBACK_MESSAGES[fee.baselineFallback]}
      </Alert>
    )}
  </Box>
);

/** What a link would do, from the API's preview (REV-2657): "#ORIG
 * resubmitted as #NEW", the original's success fee before and after, and a
 * warning for the corrected claim's remits dated on or before the action. */
const LinkSummary: FC<{ preview: ClaimLinkPreview }> = ({ preview }) => {
  const {
    parentClaimNumber,
    childClaimNumber,
    actionOn,
    differentPatient,
    feeChange,
    remitsOnOrBeforeAction,
  } = preview;
  const parent = claimTag(parentClaimNumber);
  const child = claimTag(childClaimNumber);
  const earlyWarning = describeEarlyRemits(
    remitsOnOrBeforeAction,
    childClaimNumber,
    actionOn,
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, mb: 0.5 }}>
          {describeLink(parentClaimNumber, childClaimNumber)}
        </Typography>
        <SummaryRow label="Original">{parent}</SummaryRow>
        <SummaryRow label="Corrected">
          {child}
          {differentPatient && " (different patient, confirmed)"}
        </SummaryRow>
        <SummaryRow label="Action date">
          {formatDisplayDate(actionOn)}
        </SummaryRow>
      </Box>

      {earlyWarning && (
        <Alert severity="warning" sx={{ maxWidth: "100%" }}>
          {earlyWarning}
        </Alert>
      )}

      <EffectPanel title={`Effect on ${parent}'s success fee`}>
        {feeChange ? (
          <LinkFeeEffect fee={feeChange} parent={parent} child={child} />
        ) : (
          <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
            {parent}'s success fee is already invoiced. The fees already
            charged stay; only later payments count {child}.
          </Typography>
        )}
      </EffectPanel>
    </Box>
  );
};

export default LinkSummary;
